import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";

import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpItemService from "@plannel/services/fp/fp-item-service";
import ConfirmDialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import FpGridSetting, { findMissingRequired, focusInvalidCell } from "@plannel/pages/factory-plan/common/grid";
import { AddButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import ColorUtil from "@plannel/pages/utils/color-util";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old(ItemGroupMasterPopup) validRules required 1개(itemGrpCd) — Stock.js §REQUIRED_FIELDS 패턴.
const REQUIRED_FIELDS = [
  { field: "itemGrpCd", label: "FP_ITEM_GRP_CD" }
];

/**
 * 품목 그룹(Item Group) 조회/선택 + 관리 팝업 — RouteGroupPopup.js와 동일 패턴.
 * old는 select 전용(`ItemGroupPopup.jsx`)/master 전용(`ItemGroupMasterPopup.jsx`) 두 파일로
 * 나뉘어 있었고 둘 다 런타임 토글이 없다. new는 파일을 안 나누고 `editable` prop 하나로
 * 열릴 때의 모드를 고정한다.
 * - `editable=false`(기본, ItemTab의 itemGrpCd 셀 코드픽커에서 열 때): 선택 모드 — 읽기전용,
 *   체크박스 없음, 행 더블클릭으로만 onSelect(row) 후 닫힘.
 * - `editable=true`(ItemTab "FP_ITEM_GROUP_MST" 관리 버튼에서 열 때): 관리 모드(CUD) —
 *   컬럼 편집 가능 + 체크박스 다중선택 + Add/Remove/Save.
 *
 * props: open, onClose(), planScope, onSelect(row), editable?(기본 false)
 */
const ItemGroupPopup = ({ t, open, onClose, planScope, onSelect, editable = false }) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridOptions = useMemo(() => FpGridSetting({ title: t("FP_ITEM_GROUP_MST"), viewName: "" }), [t]);

  const columnDefs = useMemo(() => ([
    { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    { headerName: "FP_PLAN_SCOPE", field: "planScope", colId: "planScope", hide: true, type: ["nonEditableColumn"] },
    // old setCodeColumnStyle 대응 — 관리 모드에서도 신규 행(id 없음)만 코드 편집 가능, 저장된 행은 잠금.
    { headerName: t("FP_ITEM_GRP_CD"), field: "itemGrpCd", colId: "itemGrpCd", cellClass: "stringType", width: 180,
      editable: params => editable && !params.data?.id },
    { headerName: t("FP_ITEM_GRP_NM"), field: "itemGrpNm", colId: "itemGrpNm", width: 240, editable },
    { headerName: t("FP_DESC_TXT"), field: "descTxt", colId: "descTxt", width: 110, editable },
    {
      headerName: t("FP_COL_AUDIT"), groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: t("updatedTs"), field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime },
        { headerName: t("updatedBy"), field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, columnGroupShow: "open" },
        { headerName: t("createdTs"), field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, columnGroupShow: "open" },
        { headerName: t("createdBy"), field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, columnGroupShow: "open" }
      ]
    }
  ]), [t, editable]);

  const loadItemGroups = () => {
    if (!planScope) return;
    fpItemService.getItemGroups({ planScope })
      .then(response => setRowData(isEmpty(response.data) ? [] : response.data))
      .catch(e => { debug(e); setRowData([]); })
      .then(() => DataState.initialize(gridRef.current?.api));
  };

  useEffect(() => {
    if (!open || !planScope) return;
    loadItemGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, planScope]);

  const confirmSelection = (row) => { if (row) { onSelect?.(row); onClose(); } };
  const onRowDoubleClicked = (e) => { if (!editable) confirmSelection(e.data); };

  const insertRow = () => {
    const newRow = { id: null, planScope, itemGrpCd: "", itemGrpNm: "", descTxt: "" };
    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const removeRows = () => {
    gridRef.current.api.stopEditing();
    const selected = gridRef.current.api.getSelectedRows();
    if (selected.length === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }
    fpItemService.removeItemGroups(selected)
      .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") }))
      .catch(e => { debug(e); setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") }); })
      .then(() => loadItemGroups());
  };

  const saveRows = () => {
    gridRef.current.api.stopEditing();
    const changes = DataState.getAllStateData(gridRef.current.api);
    if (changes.length === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    // 실제로 비어 있는 필드만 안내한다 — common/grid.js의 findMissingRequired/focusInvalidCell(§9-2).
    const missingRequired = findMissingRequired(gridRef.current.api, REQUIRED_FIELDS);
    if (missingRequired.length > 0) {
      const labels = [...new Set(missingRequired.map(m => t(m.label)))].join("/");
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: labels }) });
      focusInvalidCell(gridRef.current.api, missingRequired[0]);
      return;
    }

    fpItemService.saveItemGroups(changes)
      .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") }))
      .catch(e => { debug(e); setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") }); })
      .then(() => loadItemGroups());
  };

  const handleDialog = (action) => {
    if (action === "SAVE") saveRows();
    else if (action === "DELETE") removeRows();
  };

  return (
    <>
      <MuiDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editable ? t("FP_ITEM_GROUP_MST") : t("FP_ITEM_GROUP_SELECT")}
        </DialogTitle>
        <DialogContent>
          {editable && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <AddButton onClick={insertRow} />
              <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
              <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
            </Box>
          )}
          <Box className="ag-theme-balham" sx={{ height: 440, width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              onRowDoubleClicked={onRowDoubleClicked}
              {...gridOptions}
              rowSelection={editable
                ? { ...gridOptions.rowSelection, headerCheckbox: true, checkboxes: true }
                : { mode: "singleRow", checkboxes: false, enableClickSelection: true }}
            />
          </Box>
        </DialogContent>
      </MuiDialog>

      <ConfirmDialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ItemGroupPopup);
